import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "minInput",
        "maxInput",
        "minHandle",
        "maxHandle",
        "track",
        "trackFill",
        "minTooltip",
        "maxTooltip",
        "yearsText",
        "clearButton"
    ]

    static values = {
        type: String,
        one: Boolean,
    }

    connect() {
        this.isMinHandleMoving = false;
        this.isMaxHandleMoving = false;
        this.hasMinHandleMoved = false;
        this.hasMaxHandleMoved = false;
        this.minTimeout = null;
        this.maxTimeout = null;

        this.minRange = parseFloat(this.minInputTarget.min) || 0;
        this.maxRange = parseFloat(this.maxInputTarget.max);
        this.stepSize = parseFloat(this.minInputTarget.step);
        this.shouldRound = true;
        this.initializeHandleInputs();
        this.dispatchChangeEvent();

        this.addEventListeners();
        
        if (this.hasClearButtonTarget) {
            this.updateClearButtonVisibility();
        }
    }

    initializeHandleInputs() {
        this.updateHandleFromInput(this.minInputTarget.value, this.minRange, this.maxRange, this.minHandleTarget);
        this.updateHandleFromInput(this.maxInputTarget.value || this.maxRange, this.minRange, this.maxRange, this.maxHandleTarget);
    }

    addEventListeners() {
        this.minInputTarget.addEventListener('keyup', this.handleMinInputKeyup.bind(this))
        this.maxInputTarget.addEventListener('keyup', this.handleMaxInputKeyup.bind(this))
        this.minInputTarget.addEventListener('input', this.handleMinInputKeyup.bind(this))
        this.maxInputTarget.addEventListener('input', this.handleMaxInputKeyup.bind(this))

        this.minHandleTarget.addEventListener('mousedown', () => {
            this.isMinHandleMoving = true
            this.hasMinHandleMoved = true
        })

        this.maxHandleTarget.addEventListener('mousedown', () => {
            this.isMaxHandleMoving = true
            this.hasMaxHandleMoved = true
        })

        this.minHandleTarget.addEventListener('touchstart', () => {
            this.isMinHandleMoving = true
            this.hasMinHandleMoved = true
        })

        this.maxHandleTarget.addEventListener('touchstart', () => {
            this.isMaxHandleMoving = true
            this.hasMaxHandleMoved = true
        })

        document.addEventListener('mousemove', this.handleMove.bind(this))
        document.addEventListener('touchmove', this.handleMove.bind(this))
        document.addEventListener('mouseup', this.handleEnd.bind(this))
        document.addEventListener('touchend', this.handleEnd.bind(this))

        this.minHandleTarget.addEventListener('keydown', this.handleKeydown.bind(this))
        this.maxHandleTarget.addEventListener('keydown', this.handleKeydown.bind(this))
    }

    updateClearButtonVisibility() {
        if (!this.hasClearButtonTarget) return;
        
        const minValue = this.minInputTarget.value;
        const maxValue = this.maxInputTarget.value;
        
        if (minValue || maxValue) {
            this.clearButtonTarget.classList.remove('hidden');
        } else {
            this.clearButtonTarget.classList.add('hidden');
        }
    }

    handleMinInputKeyup(e) {
        if (e.key === 'Backspace' || e.key === 'Delete') return
        clearTimeout(this.minTimeout)
        this.minTimeout = setTimeout(() => {
            let value = parseFloat(this.minInputTarget.value)
            if (isNaN(value) || value < this.minRange) {
                value = this.minRange
            } else if (value > parseFloat(this.maxInputTarget.value)) {
                value = this.maxInputTarget.value
            }
            value = Math.min(value, this.maxRange)
            this.updateHandleFromInput(value, this.minRange, this.maxRange, this.minHandleTarget)
            this.minInputTarget.value = value
            this.updateClearButtonVisibility();
        }, 800)
    }

    handleMaxInputKeyup(e) {
        if (e.key === 'Backspace' || e.key === 'Delete') return
        clearTimeout(this.maxTimeout)
        this.maxTimeout = setTimeout(() => {
            let value = parseFloat(this.maxInputTarget.value)
            if (isNaN(value) || value > this.maxRange) {
                value = this.maxRange
            } else if (value < parseFloat(this.minInputTarget.value)) {
                value = this.minInputTarget.value
            }
            value = Math.max(value, this.minRange)
            this.updateHandleFromInput(value, this.minRange, this.maxRange, this.maxHandleTarget)
            this.maxInputTarget.value = value
            this.updateClearButtonVisibility();
        }, 800)
    }

    handleMove(event) {
        if (!this.isMinHandleMoving && !this.isMaxHandleMoving) return

        const trackRect = this.trackTarget.getBoundingClientRect()
        let x = (event.touches ? event.touches[0].clientX : event.clientX) - trackRect.left
        x = Math.min(Math.max(0, x), trackRect.width)

        if (this.isMinHandleMoving) {
            let minVal = (x / trackRect.width) * (this.maxRange - this.minRange) + this.minRange
            minVal = Math.round(minVal / this.stepSize) * this.stepSize
            minVal = this.shouldRound ? Math.round(minVal) : parseFloat(minVal.toFixed(1))

            if (minVal >= parseFloat(this.maxInputTarget.value)) {
                minVal = parseFloat(this.maxInputTarget.value) - this.stepSize
            }

            this.minHandleTarget.style.left = ((minVal - this.minRange) / (this.maxRange - this.minRange)) * 100 + "%"
            this.minInputTarget.value = minVal
            this.updateTrackFill()
            this.updateClearButtonVisibility();
        }

        if (this.isMaxHandleMoving) {
            let maxVal = (x / trackRect.width) * (this.maxRange - this.minRange) + this.minRange
            maxVal = Math.round(maxVal / this.stepSize) * this.stepSize
            maxVal = this.shouldRound ? Math.round(maxVal) : parseFloat(maxVal.toFixed(1))

            if (maxVal <= parseFloat(this.minInputTarget.value)) {
                maxVal = parseFloat(this.minInputTarget.value) + this.stepSize
            }

            this.maxHandleTarget.style.left = ((maxVal - this.minRange) / (this.maxRange - this.minRange)) * 100 + "%"
            this.maxInputTarget.value = maxVal
            this.updateTrackFill()
            this.updateClearButtonVisibility();
        }
    }

    handleEnd() {
        if (this.isMinHandleMoving || this.isMaxHandleMoving) {
            this.dispatchChangeEvent()
            this.updateClearButtonVisibility();
        }

        this.isMinHandleMoving = false
        this.isMaxHandleMoving = false
    }

    handleKeydown(event) {
        let delta = 0
        
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            delta = this.stepSize
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            delta = -this.stepSize
        } else {
            return
        }

        event.preventDefault()

        let valueInput, handle
        if (event.target === this.minHandleTarget) {
            valueInput = this.minInputTarget
            handle = this.minHandleTarget
        } else if (event.target === this.maxHandleTarget) {
            valueInput = this.maxInputTarget
            handle = this.maxHandleTarget
        } else {
            return
        }

        let value = parseFloat(valueInput.value);
        value = Math.min(Math.max(value + delta, this.minRange), this.maxRange);
        
        this.updateHandleFromInput(value, this.minRange, this.maxRange, handle);
        valueInput.value = value;
        this.dispatchChangeEvent();
        this.updateClearButtonVisibility();
    }

    updateHandleFromInput(value, minRange, maxRange, handle) {
        const percent = ((value - minRange) / (maxRange - minRange)) * 100;
        handle.style.left = percent + "%";
        this.updateTrackFill();
    }

    updateTrackFill() {
        const minHandleLeft = parseFloat(this.minHandleTarget.style.left);
        const maxHandleLeft = parseFloat(this.maxHandleTarget.style.left);

        this.trackFillTarget.style.left = minHandleLeft + "%";
        this.trackFillTarget.style.width = (maxHandleLeft - minHandleLeft) + "%";
    }

    dispatchChangeEvent() {
        const event = new CustomEvent(`${this.typeValue}-range:change`, {
            detail: {
                min: parseFloat(this.minInputTarget.value),
                max: parseFloat(this.maxInputTarget.value)
            },
            bubbles: true
        });
        this.element.dispatchEvent(event);
        
        const changeEvent = new Event('change', { bubbles: true });
        this.maxInputTarget.dispatchEvent(changeEvent);
    }

    resetSlider() {
        this.minInputTarget.value = null;
        this.updateHandleFromInput(this.minRange, this.minRange, this.maxRange, this.minHandleTarget);

        this.maxInputTarget.value = null;
        this.updateHandleFromInput(this.maxRange, this.minRange, this.maxRange, this.maxHandleTarget);
        
        this.updateTrackFill();
        this.dispatchChangeEvent();
        
        if (this.hasClearButtonTarget) {
            this.clearButtonTarget.classList.add('hidden');
        }
    }
}